import * as _ from 'underscore'

import { Enums } from '../common/enums'

import { DataProvider, ConvertHelper, TypeHelper } from '../../helpers'

import { OpDataList } from '../util/op-data-list'
import { IOpObject, IReferenceType, IOpChangeState, IOpData, ICopyable } from '../util/interfaces'
import { Utils } from '../util/utils'

import { OpDepositoryElement } from './op-depository-element'
import { OpDistributor } from './op-distributor'
import { OpFile } from './op-file'
import { OpLocation } from './op-location'
import { OpOperator } from './op-operator'
import { OpPackageData } from './op-package-data'
import { OpPackageStatus } from './op-package-status'

export class OpPackage implements IReferenceType, IOpChangeState, IOpObject, ICopyable<OpPackage> {

    //#region Properties

    public code: string
    public creationDate: Date
    public idDistributor: number
    public idOperatorCreator: number
    public idOperatorReceiver: number
    public idPackage: number
    public idPackageStatus: number
    public name: string
    public receiveDate: Date
    public received: boolean
    public sendDate: Date

    //#endregion


    //#region Navigation Properties

    private _operatorCreator: OpOperator
    public get operatorCreator(): OpOperator {
        return this._operatorCreator
    }
    public set operatorCreator(value: OpOperator) {
        this._operatorCreator = value
        this.idOperatorCreator = (value == null) ? 0 : value.idOperator;
    }

    private _operatorReceiver: OpOperator
    public get operatorReceiver(): OpOperator {
        return this._operatorReceiver
    }
    public set operatorReceiver(value: OpOperator) {
        this._operatorReceiver = value
        this.idOperatorReceiver = (value == null) ? null : value.idOperator
    }

    private _distributor: OpDistributor
    public get distributor(): OpDistributor {
        return this._distributor
    }
    public set distributor(value: OpDistributor) {
        this._distributor = value
        this.idDistributor = (value == null) ? 0 : value.idDistributor
    }

    private _packageStatus: OpPackageStatus
    public get packageStatus(): OpPackageStatus {
        return this._packageStatus
    }
    public set packageStatus(value: OpPackageStatus) {
        this._packageStatus = value
        this.idPackageStatus = (value == null) ? 0 : value.idPackageStatus
    }

    private _operatorAcceptingOrder: OpOperator
    public get operatorAcceptingOrder(): OpOperator {
        return this._operatorAcceptingOrder
    }
    public set operatorAcceptingOrder(value: OpOperator) {
        this._operatorAcceptingOrder = value
    }

    private _operatorCompletingOrder: OpOperator
    public get operatorCompletingOrder(): OpOperator {
        return this._operatorCompletingOrder
    }
    public set operatorCompletingOrder(value: OpOperator) {
        this._operatorCompletingOrder = value
    }

    private _receiptConfirmation: OpFile
    public get receiptConfirmation(): OpFile {
        return this._receiptConfirmation
    }
    public set receiptConfirmation(value: OpFile) {
        this._receiptConfirmation = value
    }

    private _depositoryLocation: OpLocation
    public get depositoryLocation(): OpLocation {
        return this._depositoryLocation
    }
    public set depositoryLocation(value: OpLocation) {
        this._depositoryLocation = value
    }

    //#endregion


    //#region Custom Properties

    public get fromAIUT(): boolean {
        return this.dataList.getValue(Enums.DataType.PACKAGE_DIRECTION)
    }

    public set fromAIUT(value: boolean) {
        this.dataList.setValue(value, Enums.DataType.PACKAGE_DIRECTION)
    }

    public get address(): string {
        return this.dataList.getValue(Enums.DataType.PACKAGE_ADDRESS)
    }

    public set address(value: string) {
        this.dataList.setValue(value, Enums.DataType.PACKAGE_ADDRESS)
    }

    public contentList: OpDepositoryElement[] = []
    public relatedPackageList: OpPackage[] = []

    //#endregion


    //#region Ctor

    constructor(opObj: any) {
        if (opObj instanceof OpPackage) {
            this.copy(<OpPackage>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }

    //#endregion


    //#region IOpChangeState Implementation

    public opState: Enums.OpChangeState

    //#endregion


    //#region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdPackage
    }

    public getReferenceKey(): number {
        return this.idPackage
    }

    public getReferenceValue(): any {
        return this
    }

    //#endregion


    //#region IOpObject Implementation

    public get idObject(): any {
        return this.idPackage
    }
    public set idObject(idObject: any) {
        this.idPackage = idObject
    }

    public dataList: OpDataList<OpPackageData> = new OpDataList<OpPackageData>()

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string

    //#endregion


    //#region IJsonSerializable Implementation

    public fromJSON(opObj: any, compressed: boolean = false): void {

        if (opObj) {
            this.idPackage = opObj.ID_PACKAGE
            this.name = opObj.NAME
            this.creationDate = opObj.CREATION_DATE ? ConvertHelper.stringToDate(opObj.CREATION_DATE) : null
            this.code = opObj.CODE

            this.sendDate = opObj.SEND_DATE ? ConvertHelper.stringToDate(opObj.SEND_DATE) : null
            this.idOperatorCreator = opObj.ID_OPERATOR_CREATOR
            this.idOperatorReceiver = opObj.ID_OPERATOR_RECEIVER
            this.received = opObj.RECEIVED

            this.receiveDate = opObj.RECEIVE_DATE ? ConvertHelper.stringToDate(opObj.RECEIVE_DATE) : null
            this.idDistributor = opObj.ID_DISTRIBUTOR
            this.idPackageStatus = opObj.ID_PACKAGE_STATUS

            if (opObj.DataList) {
                this.dataList.push(...opObj.DataList.map(data => new OpPackageData(data)))
            } else {
                this.dataList = new OpDataList<OpPackageData>()
            }

            if (opObj.ContentList != null) {
                this.contentList = opObj.ContentList.map((depositoryElement: any) => new OpDepositoryElement(depositoryElement))
            }

            this.relatedPackageList = opObj.RelatedPackageList != null ? opObj.RelatedPackageList.map((packageObj: any) => new OpPackage(packageObj)) : null
            this._depositoryLocation = opObj._DepositoryLocation != null ? new OpLocation(opObj._DepositoryLocation) : null

            this.dynamicValues = opObj.dynamicValues
            this.opState = opObj.OpState
        } else {
            this.idPackage = 0
            this.opState = Enums.OpChangeState.New
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            CREATION_DATE: this.creationDate != null ? ConvertHelper.dateToString(this.creationDate, { netFormat: true, includeTimeZone: true }) : null,
            ContentList: this.contentList != null ? this.contentList.map((content: OpDepositoryElement) => content.toJSON(compressed)) : [],
            DataList: this.dataList ? this.dataList.ToJSON(compressed) : null,

            ID_DISTRIBUTOR: this.idDistributor,
            ID_OPERATOR_CREATOR: this.idOperatorCreator,
            ID_OPERATOR_RECEIVER: this.idOperatorReceiver,
            ID_PACKAGE: this.idPackage,
            ID_PACKAGE_STATUS: this.idPackageStatus,

            CODE: this.code,
            NAME: this.name,
            OpState: this.opState,
            RECEIVED: this.received,
            RECEIVE_DATE: this.receiveDate ? ConvertHelper.dateToString(this.receiveDate, { netFormat: true, includeTimeZone: true }) : null,
            SEND_DATE: this.sendDate != null ? ConvertHelper.dateToString(this.sendDate, { netFormat: true, includeTimeZone: true }) : null,

            _DepositoryLocation: this.depositoryLocation != null ? this.depositoryLocation.toJSON(compressed) : null,

            dynamicValues: this.dynamicValues,
        }
    }

    //#endregion


    //#region toString

    public toString(): string {
        let result: string = this.name;

        if (this.code != null) {
            result += '; ' + this.code
        }

        return result

    }

    //#endregion


    //#region ICopyable Implementation

    public copy(source: OpPackage, includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): void {
        if (source == null) {
            return null
        }

        if (includeNavigationProperties) {
            if (source.depositoryLocation != null) {
                this.depositoryLocation = source.depositoryLocation.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }
        }

        if (includeCustomProperties) {
            if (source.contentList != null) {
                this.contentList = _.map(source.contentList, (contentItem: OpDepositoryElement) => contentItem.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
            }

            if (source.relatedPackageList != null) {
                this.relatedPackageList = _.map(source.relatedPackageList, (relatedPackage: OpPackage) => relatedPackage.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
            }
        }

        if (includeDataList && source.dataList != null) {
            this.dataList = source.dataList.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
        }

        if (includeDynamicValues) {
            this.dynamicValues = TypeHelper.clone(source.dynamicValues, true)
        }

        this.code = source.code
        this.creationDate = source.creationDate
        this.idDistributor = source.idDistributor
        this.idOperatorCreator = source.idOperatorCreator
        this.idOperatorReceiver = source.idOperatorReceiver
        this.idPackage = source.idPackage
        this.idPackageStatus = source.idPackageStatus
        this.name = source.name
        this.receiveDate = source.receiveDate
        this.received = source.received
        this.sendDate = source.sendDate
        this.opState = source.opState
    }

    public clone(includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpPackage {
        const newPackage = new OpPackage(null)

        newPackage.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return newPackage
    }

    //#endregion


    //#region loadNavigationProperties

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpPackage[]): JQueryPromise<OpPackage[]> {
        return Utils.loadDataListNavigationProperties(dataProvider, list)
    }

    //#endregion


    //#region fromIOpObject

    public static fromIOpObject(object: IOpObject): OpPackage {
        if (object != null) {
            const newObject: OpPackage = new OpPackage(null)

            newObject.idPackage = object.idObject
            newObject.opState = object.opState
            newObject.dataList.push(...object.dataList.toArray().map((data: IOpData) => OpPackageData.fromIOpData(data)))

            return newObject
        }

        return null
    }

    //#endregion
}